import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography
} from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, SendOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { saveSettings, testSend } from "./api";
import { channelLabels, describeReason } from "./labels";

const ChannelStatus = ({ status }) => {
  const { t } = useTranslation("dashboard");
  if (!status) return null;
  if (status.configured)
    return (
      <Tag color="success">
        {status.source === "platform" ? t("Ready (platform account)") : t("Ready")}
      </Tag>
    );
  return <Tag>{status.reason === "disabled" ? t("Off") : t("Not configured")}</Tag>;
};

/** Secret field: shows that a value is stored without revealing it; empty keeps it. */
const SecretInput = ({ stored, ...props }) => {
  const { t } = useTranslation("dashboard");
  return (
    <Input.Password
      autoComplete="new-password"
      placeholder={stored ? t("Stored – leave empty to keep") : ""}
      {...props}
    />
  );
};

const ChannelSettings = ({ settings, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const labels = channelLabels(t);
  const [form] = Form.useForm();
  const [order, setOrder] = useState(settings.channelOrder);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState({ channel: "email", to: "" });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const provider = Form.useWatch(["sms", "provider"], form);

  useEffect(() => {
    setOrder(settings.channelOrder);
    form.setFieldsValue({
      email: { enabled: settings.email.enabled },
      sms: {
        enabled: settings.sms.enabled,
        provider: settings.sms.provider,
        sender: settings.sms.sender,
        twilioAccountSid: settings.sms.twilioAccountSid,
        twilioMessagingServiceSid: settings.sms.twilioMessagingServiceSid,
        sevenApiKey: "",
        twilioAuthToken: ""
      },
      whatsapp: {
        enabled: settings.whatsapp.enabled,
        phoneNumberId: settings.whatsapp.phoneNumberId,
        language: settings.whatsapp.language,
        accessToken: ""
      }
    });
  }, [settings]);

  const move = (index, delta) => {
    const next = [...order];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item);
    setOrder(next);
  };

  const save = async values => {
    setBusy(true);
    try {
      const res = await saveSettings({ ...values, channelOrder: order });
      form.setFieldsValue({
        sms: { sevenApiKey: "", twilioAuthToken: "" },
        whatsapp: { accessToken: "" }
      });
      Success(t("Settings saved"));
      onSaved(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await testSend(test);
      setTestResult(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setTesting(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={save}>
      <Card size="small" className="nt-card" title={t("Order of the channels")}>
        <Typography.Paragraph type="secondary">
          {t(
            "Each message goes out on the first channel that is ready, that the customer agreed to and for which we have their number or address. If the provider fails, the next channel is tried."
          )}
        </Typography.Paragraph>
        <ol className="nt-order">
          {order.map((channel, index) => (
            <li key={channel}>
              <span className="nt-order-name">{labels[channel]}</span>
              <ChannelStatus status={settings.status[channel]} />
              <Space size={4} className="nt-order-actions">
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  aria-label={t("Move up")}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                />
                <Button
                  size="small"
                  icon={<ArrowDownOutlined />}
                  aria-label={t("Move down")}
                  disabled={index === order.length - 1}
                  onClick={() => move(index, 1)}
                />
              </Space>
            </li>
          ))}
        </ol>
      </Card>

      <Card
        size="small"
        className="nt-card"
        title={labels.email}
        extra={<ChannelStatus status={settings.status.email} />}
      >
        <Form.Item
          name={["email", "enabled"]}
          valuePropName="checked"
          label={t("Send customer messages by email")}
          extra={t(
            "Uses the sender configured under Settings → Email (system mailbox or your own SMTP server)."
          )}
        >
          <Switch />
        </Form.Item>
      </Card>

      <Card
        size="small"
        className="nt-card"
        title={labels.sms}
        extra={<ChannelStatus status={settings.status.sms} />}
      >
        <Form.Item name={["sms", "enabled"]} valuePropName="checked" label={t("Send SMS")}>
          <Switch />
        </Form.Item>
        <Form.Item name={["sms", "provider"]} label={t("SMS provider")}>
          <Radio.Group
            options={[
              { value: "", label: t("Platform account") },
              { value: "seven", label: "seven.io" },
              { value: "twilio", label: "Twilio" }
            ]}
            optionType="button"
          />
        </Form.Item>
        {provider === "" && (
          <Alert
            className="nt-gap"
            type="info"
            showIcon
            message={
              settings.status.sms.source === "platform" && settings.status.sms.configured
                ? t("SMS are sent through the platform's account.")
                : t(
                    "No platform SMS account is set up. Choose a provider and enter your access data."
                  )
            }
          />
        )}
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              name={["sms", "sender"]}
              label={t("Sender")}
              extra={t(
                "Up to 11 letters or digits (seven.io), or your Twilio number in international format."
              )}
            >
              <Input maxLength={20} />
            </Form.Item>
          </Col>
          {provider === "seven" && (
            <Col xs={24} md={12}>
              <Form.Item name={["sms", "sevenApiKey"]} label={t("seven.io API key")}>
                <SecretInput stored={settings.sms.hasSevenApiKey} />
              </Form.Item>
            </Col>
          )}
          {provider === "twilio" && (
            <>
              <Col xs={24} md={12}>
                <Form.Item name={["sms", "twilioAccountSid"]} label={t("Twilio Account SID")}>
                  <Input maxLength={64} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name={["sms", "twilioAuthToken"]} label={t("Twilio Auth Token")}>
                  <SecretInput stored={settings.sms.hasTwilioAuthToken} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name={["sms", "twilioMessagingServiceSid"]}
                  label={t("Twilio Messaging Service SID (optional)")}
                >
                  <Input maxLength={64} />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </Card>

      <Card
        size="small"
        className="nt-card"
        title={labels.whatsapp}
        extra={<ChannelStatus status={settings.status.whatsapp} />}
      >
        <Typography.Paragraph type="secondary">
          {t(
            "WhatsApp Business (Meta Cloud API). Businesses may only start a conversation with templates approved by Meta: enter the template name for each message type under Templates."
          )}
        </Typography.Paragraph>
        <Form.Item
          name={["whatsapp", "enabled"]}
          valuePropName="checked"
          label={t("Send WhatsApp messages")}
        >
          <Switch />
        </Form.Item>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name={["whatsapp", "phoneNumberId"]} label={t("Phone number ID")}>
              <Input maxLength={40} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name={["whatsapp", "accessToken"]} label={t("Access token")}>
              <SecretInput stored={settings.whatsapp.hasAccessToken} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name={["whatsapp", "language"]} label={t("Template language")}>
              <Select
                options={[
                  { value: "de", label: "Deutsch (de)" },
                  { value: "en", label: "English (en)" },
                  { value: "tr", label: "Türkçe (tr)" },
                  { value: "ar", label: "العربية (ar)" }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <div className="nt-actions">
        <Button type="primary" htmlType="submit" loading={busy}>
          {t("Save")}
        </Button>
      </div>

      <Card size="small" className="nt-card" title={t("Test message")}>
        <Typography.Paragraph type="secondary">
          {t(
            "Sends a sample appointment reminder to an address of your choice. Save your changes first."
          )}
        </Typography.Paragraph>
        <Row gutter={[8, 8]}>
          <Col xs={24} md={6}>
            <Select
              className="nt-full"
              value={test.channel}
              onChange={channel => setTest(prev => ({ ...prev, channel }))}
              options={["email", "sms", "whatsapp"].map(c => ({ value: c, label: labels[c] }))}
            />
          </Col>
          <Col xs={24} md={12}>
            <Input
              value={test.to}
              onChange={e => setTest(prev => ({ ...prev, to: e.target.value }))}
              placeholder={test.channel === "email" ? "name@example.de" : "0170 1234567"}
            />
          </Col>
          <Col xs={24} md={6}>
            <Button
              block
              icon={<SendOutlined />}
              loading={testing}
              disabled={!test.to.trim()}
              onClick={sendTest}
            >
              {t("Send test")}
            </Button>
          </Col>
        </Row>
        {testResult && (
          <Alert
            className="nt-gap"
            showIcon
            type={
              testResult.status === "sent"
                ? "success"
                : testResult.status === "failed"
                  ? "error"
                  : "warning"
            }
            message={
              testResult.status === "sent"
                ? t("Sent via {{channel}}.", {
                    channel: labels[testResult.channel] || testResult.channel
                  })
                : testResult.status === "failed"
                  ? testResult.reason
                  : describeReason(testResult.reason, t)
            }
          />
        )}
      </Card>
    </Form>
  );
};

export default ChannelSettings;
